import { useCallback, useMemo } from "react";
import {
  useDispatch,
  useSelector,
} from "@adsk/acc-issues-core/state/redux/hooks";
import { CommonUtil } from "@adsk/docs-js-sdk";
import {
  fetchDocumentByVersionUrn,
  selectPlacementCreatedAtRevision,
} from "@adsk/acc-issues-core/state/redux/ducks/documents";
import { FetchStatus } from "@adsk/acc-issues-core/state/redux/fetchHelpers";
import { Issue } from "@adsk/acc-issues-types";
import {
  getFileCreatedAtVersion,
  getFilePlacementUrn,
} from "@adsk/acc-issues-core/utils/data/placement";

export const usePlacementCreatedAtRevision = (
  issue?: Issue
): {
  revision?: number;
  fetchStatus?: FetchStatus;
  fetchCreatedAtRevision: () => void;
} => {
  const dispatch = useDispatch();

  const { lineageUrn, versionNumber } = useMemo(() => {
    return {
      lineageUrn: getFilePlacementUrn(issue),
      versionNumber: getFileCreatedAtVersion(issue),
    };
  }, [issue]);

  const versionUrn = useMemo(
    () =>
      (lineageUrn &&
        versionNumber !== undefined &&
        CommonUtil.lineage2Version(lineageUrn, versionNumber)) ||
      undefined,
    [lineageUrn, versionNumber]
  );

  const { document, fetchStatus } = useSelector(
    useMemo(() => selectPlacementCreatedAtRevision(versionUrn), [versionUrn])
  );

  const revision: number | undefined = document?.version?.revisionNumber;

  const fetchCreatedAtRevision = useCallback(() => {
    if (!versionUrn) {
      return;
    }
    if (fetchStatus !== undefined) {
      return;
    }

    // Defer so the Tooltip can open with "Loading..." before Redux triggers a
    // re-render wave that prevents the list badge tooltip from showing on first hover.
    setTimeout(() => {
      dispatch(fetchDocumentByVersionUrn(versionUrn));
    }, 0);
  }, [dispatch, versionUrn, fetchStatus]);

  return {
    revision,
    fetchStatus,
    fetchCreatedAtRevision,
  };
};
